import { ajax } from "../async";
import { wcfConfig } from "../WcfConfig";
/**
 * Loads the content of the given url and provides it via blob url.
 * If the content is no longer used `URL.revokeObjectURL()` has to be called to release resources.
 * Note: This function will trigger `async.ajaxErrorHandler` instead of `loadContentErrorHandler` because ajax is used internally.
 */
export async function loadContentAsBlobUrl(pUrl) {
    const tBlob = await ajax("GET", pUrl, undefined, { responseType: "blob" });
    return URL.createObjectURL(tBlob);
}
/**
 * Loads and executes JavaScript.
 * pAsync can be set to false to ensure correct execution order of loaded script. (default: true)
 */
export function loadScript(pUrl, pAsync = true) {
    const tPromise = loadContent(pUrl, (pOnLoad, pOnError) => {
        const tScript = document.createElement("script");
        tScript.type = "text/javascript";
        tScript.onload = pOnLoad;
        tScript.onerror = pOnError;
        tScript.async = pAsync;
        tScript.src = pUrl;
        return (tScript);
    });
    return (tPromise);
}
/**
 * Loads a Cascading Style Sheet.
 */
export function loadStyleSheet(pUrl) {
    const tPromise = loadContent(pUrl, (pOnLoad, pOnError) => {
        const tLink = document.createElement("link");
        tLink.rel = "stylesheet";
        tLink.type = "text/css";
        tLink.onload = pOnLoad;
        tLink.onerror = pOnError;
        tLink.href = pUrl;
        return (tLink);
    });
    return (tPromise);
}
/**
 * Submits the given fields as a form to the given url. This will change the window location.
 */
export function submitForm(pMethod, pUrl, pFields) {
    const tForm = document.createElement("form");
    tForm.setAttribute("method", pMethod);
    tForm.setAttribute("action", pUrl);
    pFields.forEach((pValue, pKey) => {
        const tField = document.createElement("input");
        tField.setAttribute("type", "hidden");
        tField.setAttribute("name", pKey);
        tField.setAttribute("value", pValue);
        tForm.appendChild(tField);
    });
    document.body.appendChild(tForm);
    tForm.submit();
}
/**
 * Helper function for content loading which performs retry attempts and calls the loadContentErrorHandler.
 */
function loadContent(pUrl, pCreateElement) {
    const tMaxRetries = wcfConfig.ajaxDefaultOptions.retryAttempts;
    const load = (pResolve, pReject, pTotalRetryCount, pRetryCount) => {
        const onResolve = () => pResolve();
        const onError = () => {
            //remove faulty element from dom
            document.body.removeChild(tElement);
            //automatically retry to load the content
            if (pRetryCount < tMaxRetries) {
                setTimeout(() => {
                    load(pResolve, pReject, pTotalRetryCount + 1, pRetryCount + 1);
                }, wcfConfig.ajaxGetRetryDelay(pRetryCount + 1));
                return;
            }
            //delegate error information to error handler
            if (wcfConfig.loadContentErrorHandler != null) {
                const tErrorInfo = {
                    url: pUrl,
                    retryCount: pTotalRetryCount,
                    retry: () => load(pResolve, pReject, pTotalRetryCount + 1, 0),
                    reject: () => pReject(new Error("Failed to load content: " + pUrl))
                };
                wcfConfig.loadContentErrorHandler(tErrorInfo);
            }
            else {
                pReject(new Error("Failed to load content: " + pUrl));
            }
        };
        const tElement = pCreateElement(onResolve, onError);
        document.body.appendChild(tElement);
    };
    const tPromise = new Promise((pResolve, pReject) => {
        load(pResolve, pReject, 0, 0);
    });
    return (tPromise);
}
